import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Row,
  Col,
  Timeline,
  Button,
  Space,
  Typography,
  Avatar,
  Tooltip,
} from 'antd';
import {
  TwitterOutlined,
  PictureFilled,
  TabletFilled,
  PlusSquareOutlined,
  CheckCircleOutlined,
  PlusCircleOutlined,
  ArrowLeftOutlined,
  LoginOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import VisibilitySensor from 'react-visibility-sensor';

import ArticleCard from '../Card/Article';
import ImageCard from '../Card/ImageCard';
import TwitterCard from '../Card/TwitterCard';

import { DateContext } from '../Context/DateContext';
import { FeedContext } from '../Context/FeedContext';
import { DrawerContext } from '../Context/DrawerContext';
import { ThemeContext } from '../Context/ThemeContext';
import { ProfileContext } from '../Context/ProfileContext';

import WelcomeCard from '../Card/WelcomeCard';
import HelmetSeo from '../../Assets/Utilities/HelmetSeo';
import { app, authApp } from '../../Config';

const { Text } = Typography;
export default function MixedTimeline() {
  const history = useHistory();

  const { openDrawer } = useContext(DrawerContext);
  const { currentDate, setCurrentDate } = useContext(DateContext);
  const {
    articles,
    fetchMoreArticles,
    loadingMoreArticles,
    user,
    addPostLoader,
  } = useContext(FeedContext);
  const { theme } = useContext(ThemeContext);
  const {
    profile,
    openProfileDrawer,
    personalProfile,
    followUser,
    followingLoader,
    isFollowing,
    loggedin,
  } = useContext(ProfileContext);

  let emptyfeed = !(articles.length > 0);

  useEffect(() => {
    emptyfeed = !(articles.length > 0);
  }, [articles]);

  return (
    <div>
      <HelmetSeo
        title={`${profile.userData.user.name.fullName} | Interact`}
        description='Stay upto date with friends, followers and Global family, start exploring!'
        url={`${window.location.href}`}
        image={`${profile.userData.user.picture.replace(
          's96-c',
          's512-c',
          true
        )}`}
      />
      <Timeline id='itemContainer'>
        {!emptyfeed ? (
          articles.map((item) => {
            switch (item.postType) {
              case 'twitter_card': {
                // make twitter ready
                const tweet = {};
                tweet.text = encodeURIComponent(
                  item.description.length > 140
                    ? `${item.description.substring(0, 130)}...`
                    : item.description
                );
                tweet.url = encodeURIComponent(
                  `https://interact.rajchandra.me/updates/${item.uid}`
                );
                tweet.via = encodeURIComponent('');
                tweet.hashtags = encodeURIComponent('');
                const query = `text=${tweet.text}&url=${tweet.url}&via=${tweet.via}&hashtags=${tweet.hashtags}`;
                return (
                  <VisibilitySensor
                    key={item.uid}
                    onChange={(isVisible) => {
                      if (isVisible && item.createdAt !== currentDate) {
                        setCurrentDate(item.createdAt);
                      }
                    }}
                  >
                    <Timeline.Item
                      key={item.uid}
                      style={{ padding: '0px' }}
                      dot={
                        item.privacy !== 'private' ? (
                          <a
                            className='twitter-share-button'
                            target='_blank'
                            rel='noopener noreferrer'
                            href={`https://twitter.com/intent/tweet?${query}`}
                          >
                            <TwitterOutlined
                              style={{
                                background: theme.content.bg,
                                fontSize: '32px',
                              }}
                            />
                          </a>
                        ) : (
                          <EyeInvisibleOutlined
                            style={{
                              cursor: 'not-allowed',
                              background: theme.content.bg,
                              fontSize: '32px',
                              color: '#1890ff',
                            }}
                          />
                        )
                      }
                      color={theme.label === 'Dark' ? '#794bc4' : ''}
                    >
                      <TwitterCard item={item} />
                    </Timeline.Item>
                  </VisibilitySensor>
                );
              }

              case 'article_card': {
                return (
                  <VisibilitySensor
                    key={item.uid}
                    onChange={(isVisible) => {
                      if (isVisible && item.createdAt !== currentDate) {
                        setCurrentDate(item.createdAt);
                      }
                    }}
                  >
                    <Timeline.Item
                      key={item.uid}
                      dot={<TabletFilled style={{ fontSize: '2rem' }} />}
                    >
                      <ArticleCard item={item} />
                    </Timeline.Item>
                  </VisibilitySensor>
                );
              }

              case 'media_card': {
                return (
                  <VisibilitySensor
                    key={item.uid}
                    onChange={(isVisible) => {
                      if (isVisible && item.createdAt !== currentDate) {
                        setCurrentDate(item.createdAt);
                      }
                    }}
                  >
                    <Timeline.Item
                      key={item.uid}
                      dot={<PictureFilled style={{ fontSize: '2rem' }} />}
                    >
                      <ImageCard item={item} />
                    </Timeline.Item>
                  </VisibilitySensor>
                );
              }

              default: {
                return (
                  <VisibilitySensor
                    key={item.uid}
                    onChange={(isVisible) => {
                      if (isVisible && item.createdAt !== currentDate) {
                        setCurrentDate(item.createdAt);
                      }
                    }}
                  >
                    <Timeline.Item
                      key={item.uid}
                      dot={<TwitterOutlined style={{ fontSize: '2rem' }} />}
                    >
                      <TwitterCard item={item} />
                    </Timeline.Item>
                  </VisibilitySensor>
                );
              }
            }
          })
        ) : (
          <Col
            xs={24}
            style={{
              height: '100%',
              padding: '1rem 0 1rem 0',
              filter: loadingMoreArticles ? 'blur(8px)' : null,
            }}
          >
            {personalProfile ? (
              <div>
                <WelcomeCard />
              </div>
            ) : (
              <div>
                <img
                  src={`./images/nothing-here-${theme.label}.jpg`}
                  alt='There is nothing here!'
                  width='100%'
                  height='100%'
                />
                <Col
                  style={{
                    color: theme.content.text,
                    margin: '1rem 0 0 0',
                    fontSize: '1.2rem',
                  }}
                >
                  Why so empty? <br />
                  <ul>
                    <li>This user doesn't have an account on this platform</li>
                    <li>This user has not posted any updates yet</li>
                    <li>The user limits who can see their updates</li>
                    <li>
                      If you think there is an error, please contact support -{' '}
                      <a href='mailto:mail@rajchandra.me'>mail@rajchandra.me</a>{' '}
                    </li>
                  </ul>
                </Col>
              </div>
            )}
          </Col>
        )}
        {!emptyfeed && articles.length < 5
          ? [...Array(5 - articles.length).keys()].map((key) => {
              return (
                <Timeline.Item key={key} style={{}}>
                  <Row
                    style={{
                      padding: '1rem 0 0 0',
                    }}
                  >
                    <Col
                      xs={24}
                      style={{
                        color: theme.content.text,
                        textAlign: 'center',
                        padding: '2rem',
                        height: '100px',
                        border: `2px dashed ${theme.content.secondaryText}`,
                        borderRadius: '8px',
                      }}
                    >
                      Add More Updates
                    </Col>
                  </Row>
                </Timeline.Item>
              );
            })
          : null}
        <Space>
          {articles.length >= 10 ? (
            <Button
              type='primary'
              shape='round'
              size='large'
              loading={loadingMoreArticles}
              disabled={loadingMoreArticles}
              onClick={() => fetchMoreArticles()}
            >
              Load More
            </Button>
          ) : null}
          {personalProfile ? null : (
            // <Button
            //     type='primary'
            //     shape='round'
            //     size='large'
            //     icon={<ReloadOutlined />}
            //     onClick={() => window.location.reload()}
            // >
            //     Check for Updates
            // </Button>
            <Button
              type='primary'
              shape='round'
              size='large'
              icon={<ArrowLeftOutlined />}
              onClick={() => {
                window.location.href = '/';
              }}
            >
              Back to your feed
            </Button>
          )}
        </Space>
      </Timeline>
      {/* CTAs */}
      <Row
        style={{
          // position: '-webkit-sticky',
          position: 'sticky',
          bottom: '1rem',
          marginTop: '1rem',
        }}
      >
        {loggedin && personalProfile ? (
          <Col xs={22} style={{ padding: '0 0.5rem 0 0' }}>
            <Button
              block
              type='primary'
              size='large'
              shape='round'
              icon={<PlusSquareOutlined />}
              onClick={openDrawer}
              loading={addPostLoader}
            >
              {addPostLoader ? 'Submitting...' : "What's Happening?"}
            </Button>
          </Col>
        ) : null}
        {loggedin && !personalProfile ? (
          <Col xs={22} style={{ padding: '0 0.5rem 0 0' }}>
            <Button
              block
              title={`Click here to ${isFollowing ? 'Unfollow' : 'Follow'}`}
              loading={followingLoader}
              type='primary'
              size='large'
              shape='round'
              icon={
                isFollowing ? <CheckCircleOutlined /> : <PlusCircleOutlined />
              }
              onClick={() => {
                return followUser(user.uid);
              }}
            >
              {isFollowing ? 'Following' : 'Follow'}
            </Button>
          </Col>
        ) : null}
        {!loggedin ? (
          <Col xs={22} style={{ padding: '0 0.5rem 0 0' }}>
            <Button
              block
              type='primary'
              size='large'
              shape='round'
              icon={<LoginOutlined />}
              onClick={() => {
                window.location.href = `${authApp}/login?returnTo=${encodeURIComponent(
                  window.location.href
                )}`;
              }}
            >
              Login to your Account
            </Button>
          </Col>
        ) : null}
        <Col
          style={{
            cursor: 'pointer',
            textAlign: 'center',
          }}
          xs={2}
        >
          {personalProfile ? (
            <Avatar
              size='large'
              onClick={openProfileDrawer}
              icon={
                <img
                  src={profile.userData.user.picture.replace(
                    's96-c',
                    's512-c',
                    true
                  )}
                  width='25'
                  alt={profile.userData.user.name.fullName}
                />
              }
            >
              {profile.userData.user.name.fullName}
            </Avatar>
          ) : (
            <Tooltip
              placement='topRight'
              title={
                user.name.fullName
                  ? `You are viewing ${user.name.fullName}'s updates`
                  : `Loading Profile ...`
              }
              color='red'
              key='red'
            >
              <Avatar
                size='large'
                icon={
                  <img
                    src={user.picture.replace('s96-c', 's512-c', true)}
                    width='25'
                    alt={user.name.fullName}
                  />
                }
              >
                {user.name.fullName}
              </Avatar>
            </Tooltip>
          )}
        </Col>
      </Row>
    </div>
  );
}
